/**
 * Hangman Game Persistence Service
 *
 * Auto-saves game state to local storage after each guess.
 * Follows SudokuGamePersistence pattern.
 */

import {
  HangmanGameState,
  HangmanDifficulty,
  HangmanCategory,
} from '../types';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import { DataSynchronizationService } from '../../services/DataSynchronizationService';

export interface SavedHangmanGame {
  gameId: string;
  gameState: HangmanGameState;
  metadata: {
    difficulty: HangmanDifficulty;
    category: HangmanCategory;
    startedAt: number;
    lastSavedAt: number;
  };
}

export class HangmanGamePersistence {
  private storage: LocalStorageManager;
  private syncService: DataSynchronizationService | null;
  private readonly STORAGE_KEY = 'hangman_saved_games';
  private readonly AUTO_SAVE_KEY = 'hangman_auto_save';

  constructor(
    storage: LocalStorageManager,
    syncService?: DataSynchronizationService
  ) {
    this.storage = storage;
    this.syncService = syncService || null;
  }

  /** Auto-save the current game state after each guess. */
  autoSave(
    gameState: HangmanGameState,
    metadata: SavedHangmanGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedHangmanGame = {
        gameId: 'auto_save',
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };
      localStorage.setItem(this.AUTO_SAVE_KEY, JSON.stringify(savedGame));
      return true;
    } catch (error) {
      console.error('Failed to auto-save hangman game:', error);
      return false;
    }
  }

  /** Load the auto-saved game state. */
  loadAutoSave(): SavedHangmanGame | null {
    try {
      const data = localStorage.getItem(this.AUTO_SAVE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Failed to load hangman auto-save:', error);
      return null;
    }
  }

  /** Clear the auto-saved game state. */
  clearAutoSave(): boolean {
    try {
      localStorage.removeItem(this.AUTO_SAVE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear hangman auto-save:', error);
      return false;
    }
  }

  /** Check if an auto-save exists. */
  hasAutoSave(): boolean {
    return this.loadAutoSave() !== null;
  }

  /** Save a game with a specific ID. */
  saveGame(
    gameId: string,
    gameState: HangmanGameState,
    metadata: SavedHangmanGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedHangmanGame = {
        gameId,
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };

      const games = this.loadAllGames();
      const existingIndex = games.findIndex((g) => g.gameId === gameId);

      if (existingIndex >= 0) {
        games[existingIndex] = savedGame;
      } else {
        games.push(savedGame);
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(games));
      return true;
    } catch (error) {
      console.error('Failed to save hangman game:', error);
      return false;
    }
  }

  /** Load a saved game by ID. */
  loadGame(gameId: string): SavedHangmanGame | null {
    try {
      const games = this.loadAllGames();
      return games.find((g) => g.gameId === gameId) || null;
    } catch (error) {
      console.error('Failed to load hangman game:', error);
      return null;
    }
  }

  /** Load all saved games. */
  loadAllGames(): SavedHangmanGame[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Failed to load hangman games:', error);
      return [];
    }
  }

  /** Delete a saved game by ID. */
  deleteGame(gameId: string): boolean {
    try {
      const games = this.loadAllGames();
      const filtered = games.filter((g) => g.gameId !== gameId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
      return true;
    } catch (error) {
      console.error('Failed to delete hangman game:', error);
      return false;
    }
  }

  /** Clear all saved games. */
  clearAllGames(): boolean {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.AUTO_SAVE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear all hangman games:', error);
      return false;
    }
  }

  /** Sync saved games from cloud storage. */
  async syncFromCloud(): Promise<boolean> {
    try {
      if (!this.syncService) return false;
      console.warn('Cloud sync not yet implemented for hangman games');
      return false;
    } catch (error) {
      console.error('Failed to sync hangman games from cloud:', error);
      return false;
    }
  }

  /** Sync saved games to cloud storage. */
  async syncToCloud(): Promise<boolean> {
    try {
      if (!this.syncService) return false;
      console.warn('Cloud sync not yet implemented for hangman games');
      return true;
    } catch (error) {
      console.error('Failed to sync hangman games to cloud:', error);
      return false;
    }
  }
}
